// AddBoardPopUp.tsx
import React, { useState, useEffect } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
  collection,
  query,
  onSnapshot,
  updateDoc,
  doc,
  arrayUnion,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../utils/firebase";
import { Board } from "../../utils/boards";
import { useAuth } from "../../utils/AuthContext";

interface AddBoardPopUpProps {
  videoId: string;
  styleframeURL: string;
  onClose?: () => void;
}

const AddBoardPopUp: React.FC<AddBoardPopUpProps> = ({
  videoId,
  styleframeURL,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { currentUser } = useAuth();

  // Fetch boards for the current user
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    const boardsRef = collection(db, "users", currentUser.uid, "boards");
    const q = query(boardsRef);
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const boardsData: Board[] = [];
        querySnapshot.forEach((docSnap) => {
          boardsData.push({ id: docSnap.id, ...docSnap.data() } as Board);
        });
        setBoards(boardsData);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching boards:", err);
        setError("Erreur lors du chargement des tableaux.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Filter boards by search term (safely handling undefined titles)
  const filteredBoards = boards.filter((board) =>
    (board.title || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // When a board is clicked, add the styleframe as a new picture
  const handleAddToBoard = async (board: Board) => {
    if (!currentUser) {
      setError("Vous devez être connecté pour ajouter une image.");
      return;
    }
    try {
      setLoading(true);
      const boardDocRef = doc(db, "users", currentUser.uid, "boards", board.id);

      // Create a new Picture object as defined in your boards.ts
      const newPicture = {
        url: styleframeURL,
        reference: videoId, // Using videoId as the reference (adjust if needed)
        createdAt: Timestamp.now(),
      };

      await updateDoc(boardDocRef, {
        pictures: arrayUnion(newPicture),
      });
      setSuccess("Image ajoutée avec succès !");
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      console.error("Error adding image to board:", err);
      setError("Erreur lors de l'ajout de l'image au tableau.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col px-4 py-4 gap-4 bg-neutral-50 border border-gray-300 rounded-md shadow-lg">
      <h5 className="text-lg font-semibold">Ajouter à un tableau</h5>
      <div className="flex items-center bg-gray-100 rounded-full px-3 py-2 max-w-[300px]">
        <MagnifyingGlassIcon className="w-5 h-5 text-neutral-900" />
        <input
          type="text"
          placeholder="Rechercher un tableau"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="ml-2 w-full bg-transparent border-none outline-none"
          aria-label="Rechercher un tableau"
        />
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      {success && <p className="text-green-500 text-sm">{success}</p>}
      {loading && <p className="text-gray-500 text-sm">Chargement...</p>}
      <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
        {filteredBoards.length > 0 ? (
          filteredBoards.map((board) => (
            <div
              key={board.id}
              onClick={() => handleAddToBoard(board)}
              className="cursor-pointer flex items-center gap-4 border p-2 rounded hover:bg-gray-100 transition"
            >
              <img
                src={board.thumbnail}
                alt={board.title || "Aperçu du tableau"}
                className="w-16 h-16 object-cover rounded"
              />
              <div className="flex flex-col">
                <span className="font-medium">{board.title || "Sans titre"}</span>
              </div>
            </div>
          ))
        ) : (
          !loading && <p className="text-gray-500 text-sm">Aucun tableau trouvé</p>
        )}
      </div>
    </div>
  );
};

export default AddBoardPopUp;
